/**
 * useUsage Hook
 * 
 * Provides usage tracking state and methods for authenticated users.
 * Fetches calculation usage data from the database and provides computed values.
 */

import { useState, useEffect, useCallback } from 'react';
import { getUserUsage, type UserUsage } from '../services/calculationService';

/**
 * Return type for useUsage hook
 */
export interface UseUsageReturn {
  /** Current calculation count */
  usageCount: number;
  /** Usage limit (fetched from database, defaults to 5) */
  usageLimit: number;
  /** Remaining calculations */
  remaining: number;
  /** Whether user can make a calculation */
  canMakeCalculation: boolean;
  /** Whether usage data is being loaded */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refresh usage data from server */
  refreshUsage: () => Promise<void>;
}

/**
 * useUsage Hook
 * 
 * Fetches and manages usage tracking data for an authenticated user.
 * Automatically refreshes when userId changes.
 * 
 * @param userId - User ID (UUID) or undefined if not authenticated
 * @returns Usage tracking state and methods
 */
export function useUsage(userId: string | undefined): UseUsageReturn {
  const [usageCount, setUsageCount] = useState<number>(0);
  const [usageLimit, setUsageLimit] = useState<number>(5);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch usage data from server
   */
  const fetchUsage = useCallback(async () => {
    if (!userId) {
      // No user ID - reset to defaults
      setUsageCount(0);
      setUsageLimit(5);
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const usage: UserUsage = await getUserUsage(userId);
      setUsageCount(usage.calculationCount);
      setUsageLimit(usage.limit);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch usage data';
      setError(errorMessage);
      console.error('[useUsage] Failed to fetch usage:', err);
      // Don't reset usageCount on error (keep previous value)
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Refresh usage data manually
   */
  const refreshUsage = useCallback(async () => {
    await fetchUsage();
  }, [fetchUsage]);

  /**
   * Fetch usage data on mount and when userId changes
   */
  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  /**
   * Listen for usage update events (triggered after successful calculation)
   */
  useEffect(() => {
    const handleUsageUpdate = (event: CustomEvent) => {
      if (event.detail?.userId === userId) {
        // Delay refresh to allow database to commit
        setTimeout(() => {
          fetchUsage();
        }, 1000);
      }
    };

    window.addEventListener('usageUpdated', handleUsageUpdate as EventListener);
    return () => window.removeEventListener('usageUpdated', handleUsageUpdate as EventListener);
  }, [userId, fetchUsage]);

  // Compute derived values
  const remaining = Math.max(0, usageLimit - usageCount);
  const canMakeCalculation = remaining > 0;

  return {
    usageCount,
    usageLimit,
    remaining,
    canMakeCalculation,
    isLoading,
    error,
    refreshUsage,
  };
}

